import type { MeResponse } from '@/lib/api';
import type { TrialActivation } from '@/hooks/useTrialActivation';
import { Icon } from '@/components/Icon';
import { ShushMark } from '@/components/ShushMark';
import { daysWord, formatExpiresShort, hostFromLink, truncateLink } from '@/lib/format';
import { haptic } from '@/lib/telegram';

interface HomeScreenProps {
  me: MeResponse;
  trial: TrialActivation;
  navigate: (screen: 'paywall' | 'setup') => void;
  onCopy: () => void;
}

const subtitleByState: Record<MeResponse['subscription']['state'], string> = {
  active: 'канал открыт — всё работает',
  trial: 'триал активен — наслаждайся',
  expired: 'подписка истекла — но мы рядом',
  new: 'добро пожаловать. начнём с триала',
};

export function HomeScreen({ me, trial, navigate, onCopy }: HomeScreenProps) {
  const { subscription, user } = me;
  const isExpired = subscription.state === 'expired';
  const isTrial = subscription.state === 'trial';
  const isActive = subscription.state === 'active';
  const isNew = subscription.state === 'new';
  const daysLeft = isExpired ? 0 : Math.max(subscription.days_left, 0);
  const expiresFmt = formatExpiresShort(subscription.expires_at);
  const displayUrl = subscription.url;

  const handleCopy = async () => {
    if (!displayUrl) return;
    haptic('medium');
    let copied = false;
    try {
      await navigator.clipboard.writeText(displayUrl);
      copied = true;
    } catch {
      // some Telegram clients block clipboard — fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = displayUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { copied = document.execCommand('copy'); } catch { /* both paths failed */ }
      document.body.removeChild(ta);
    }
    if (copied) onCopy();
  };

  const firstNameLower = (user.first_name || 'друг').toLowerCase();
  const linkPreview = truncateLink(displayUrl);
  const linkHost = hostFromLink(displayUrl, 'de.shush.cloud');

  return (
    <div
      className="no-scrollbar"
      style={{ padding: '8px 18px 140px', overflowY: 'auto', height: '100%' }}
    >
      {/* greeting */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginTop: 6,
          marginBottom: 22,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: 32,
              lineHeight: 1,
              color: 'var(--ink)',
              letterSpacing: '-0.015em',
            }}
          >
            привет, {firstNameLower}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
            {subtitleByState[subscription.state]}
          </div>
        </div>
        <ShushMark size={32} color="var(--ink)" accent="var(--moss)" />
      </div>

      {/* status ledger */}
      <div
        style={{
          background: '#fbf8f1',
          border: '1px solid var(--paper-3)',
          borderRadius: 22,
          padding: 20,
          marginBottom: 14,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: isExpired ? 'var(--warn)' : 'var(--moss)',
              boxShadow: isExpired
                ? 'none'
                : '0 0 0 4px color-mix(in oklab, var(--moss) 20%, transparent)',
            }}
          />
          <span className="eyebrow">
            {isActive && 'подписка активна'}
            {isTrial && `триал · ${daysLeft} ${daysWord(daysLeft)}`}
            {isExpired && 'подписка истекла'}
            {isNew && 'нет активной подписки'}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
            id {user.telegram_id}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 4 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: 92,
              lineHeight: 0.85,
              color: isExpired ? 'var(--ink-3)' : 'var(--ink)',
              letterSpacing: '-0.04em',
              fontFeatureSettings: '"lnum" 1',
            }}
          >
            {isNew ? '—' : daysLeft}
          </span>
          <div style={{ paddingBottom: 12, lineHeight: 1.2 }}>
            <div
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: 24,
                color: 'var(--ink)',
                fontStyle: 'italic',
              }}
            >
              {isNew ? 'дней' : daysWord(daysLeft)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
              {isExpired && 'нужно продлить'}
              {isNew && 'триала нет'}
              {(isActive || isTrial) && expiresFmt && `до ${expiresFmt}`}
            </div>
          </div>
        </div>
      </div>

      {/* connection link card — only when there's a key to show */}
      {!isExpired && !isNew && subscription.url && (
        <div
          style={{
            background: 'var(--ink)',
            color: 'var(--paper)',
            borderRadius: 22,
            padding: '18px 18px 16px',
            marginBottom: 14,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <span className="eyebrow" style={{ color: 'rgba(244,240,232,0.55)' }}>
              твой ключ подключения
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 10.5,
                fontFamily: 'var(--f-mono)',
                color: 'var(--moss-soft)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: 'var(--moss-soft)',
                }}
              />
              живой
            </span>
          </div>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: 13,
              color: 'rgba(244,240,232,0.85)',
              wordBreak: 'break-all',
              marginBottom: 14,
              lineHeight: 1.5,
            }}
          >
            {linkPreview ? (
              linkPreview
            ) : (
              <>
                <span style={{ color: 'rgba(244,240,232,0.4)' }}>https://</span>
                {linkHost}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleCopy}
              className="btn btn-paper"
              style={{ flex: 1, height: 44, fontSize: 14 }}
            >
              <Icon name="copy" size={16} stroke="var(--ink)" sw={1.7} />
              скопировать ключ
            </button>
          </div>
        </div>
      )}

      {/* primary cta when expired/new */}
      {(isExpired || isNew || trial.created) && (
        <button
          onClick={() => {
            if (isNew || trial.created) void trial.start();
            else {
              haptic('medium');
              navigate('paywall');
            }
          }}
          disabled={trial.busy}
          className="btn btn-moss"
          style={{
            width: '100%',
            height: 56,
            marginBottom: 14,
            fontSize: 15,
            fontWeight: 500,
            justifyContent: 'space-between',
            padding: '0 22px',
            opacity: trial.busy ? 0.6 : 1,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Icon name="bolt" size={18} stroke="#fff" sw={1.8} />
            {trial.busy
              ? (trial.created ? 'готовим подключение…' : 'активируем…')
              : trial.created ? 'проверить подключение'
              : isNew ? 'начать с триала' : 'продлить подписку'}
          </span>
          <span
            style={{
              fontSize: 13,
              opacity: 0.85,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {isNew || trial.created ? 'бесплатно' : (
              <>
                {me.pricing.stars.final_amount}
                <Icon name="star" size={12} stroke="var(--stars)" fill="var(--stars)" sw={1} />
              </>
            )}
          </span>
        </button>
      )}

      {trial.error && <p role="status" style={{ color: 'var(--ink-2)', fontSize: 13 }}>{trial.error}</p>}

      {/* installation action card */}
      <div style={{ marginBottom: 14 }}>
        <ActionCard
          icon="install"
          label="установка"
          sub="3 шага · 2 минуты"
          onClick={() => {
            haptic('light');
            navigate('setup');
          }}
        />
      </div>

      {/* trust note */}
      <div
        style={{
          marginTop: 18,
          padding: '14px 16px',
          border: '1px dashed var(--paper-3)',
          borderRadius: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Icon name="lock" size={18} stroke="var(--ink-2)" sw={1.6} />
        <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          мы не храним логи. зарабатываем только на подписках —
          <span style={{ color: 'var(--ink)' }}> никакой рекламы и продажи данных</span>
        </div>
      </div>
    </div>
  );
}

interface ActionCardProps {
  icon: 'install' | 'globe' | 'devices' | 'help';
  label: string;
  sub: string;
  onClick: () => void;
}

function ActionCard({ icon, label, sub, onClick }: ActionCardProps) {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: 'none',
        cursor: 'pointer',
        background: '#fbf8f1',
        border: '1px solid var(--paper-3)',
        borderRadius: 18,
        padding: 14,
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minHeight: 110,
        width: '100%',
      }}
    >
      <Icon name={icon} size={22} stroke="var(--ink)" sw={1.5} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 3 }}>
          {label}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{sub}</div>
      </div>
    </button>
  );
}
